import { normalizeTargetId } from "@/components/RenderedDie/helpers/getAnimationTarget";
import {
  DataTypes,
  type AnimationGroup,
  type TargetedAnimation,
} from "@/types";
import {
  NumberKeyframeTrack,
  Vector3,
  VectorKeyframeTrack,
  type Interpolant,
} from "three";
import { extractAnimationKeyframes } from "./extractAnimationKeyframes";

export type SamplerOutput = {
  scale?: Vector3;
  visibility?: number;
};

export type ManifestAnimationSampler = {
  durationSeconds: number;
  sample: (t: number, out: SamplerOutput) => SamplerOutput;
};

// Build samplable interpolants from a manifest AnimationGroup so the instanced
// dice path can evaluate manifest animations without an Object3D / AnimationMixer.
//
// Currently handles `scaling` and `visibility`. `targetName` is the value of
// the source mesh's userData[ANIMATION_TARGET_ID]
export const buildManifestAnimationSampler = (
  group: AnimationGroup,
  targetName: string,
): ManifestAnimationSampler | null => {
  const matchesTarget = (targeted: TargetedAnimation) =>
    targetName.includes(normalizeTargetId(targeted.targetId));

  const scaleTargeted = group.targetedAnimations.find(
    (targeted) =>
      targeted.animation.property === "scaling" &&
      targeted.animation.dataType === DataTypes.VECTOR &&
      matchesTarget(targeted),
  );
  const visibilityTargeted = group.targetedAnimations.find(
    (targeted) =>
      targeted.animation.property === "visibility" &&
      targeted.animation.dataType === DataTypes.NUMBER &&
      matchesTarget(targeted),
  );

  if (!scaleTargeted && !visibilityTargeted) return null;

  let scaleInterpolant: Interpolant | undefined;
  let scaleOwnedOutput: Vector3 | undefined;
  let groupFps = 0;
  let clampMax = 0;

  if (scaleTargeted) {
    const { fps, times, values } = extractAnimationKeyframes(
      scaleTargeted.animation,
    );
    const track = new VectorKeyframeTrack(".scale", times, values);
    scaleInterpolant = track.createInterpolant();
    scaleOwnedOutput = new Vector3();
    groupFps = fps;
    clampMax = Math.max(clampMax, times[times.length - 1] ?? 0);
  }

  let visibilityInterpolant: Interpolant | undefined;
  if (visibilityTargeted) {
    const { fps, times, values } = extractAnimationKeyframes(
      visibilityTargeted.animation,
    );
    const track = new NumberKeyframeTrack(".visible", times, values);
    visibilityInterpolant = track.InterpolantFactoryMethodDiscrete(undefined);
    groupFps = groupFps || fps;
    clampMax = Math.max(clampMax, times[times.length - 1] ?? 0);
  }

  const durationSeconds = (group.to ?? 0) / (groupFps || 1);
  // Honor clampWhenFinished semantics on the non-instanced path: once the
  // animation runs out, sample at the final keyframe instead of looping back.
  clampMax = Math.max(clampMax, durationSeconds);

  const sample = (t: number, out: SamplerOutput): SamplerOutput => {
    const clamped = Math.min(Math.max(t, 0), clampMax);

    if (scaleInterpolant && scaleOwnedOutput) {
      const result = scaleInterpolant.evaluate(clamped) as ArrayLike<number>;
      scaleOwnedOutput.set(result[0], result[1], result[2]);
      out.scale = scaleOwnedOutput;
    }

    if (visibilityInterpolant) {
      const result = visibilityInterpolant.evaluate(
        clamped,
      ) as ArrayLike<number>;
      out.visibility = result[0] > 0.5 ? 1 : 0;
    }

    return out;
  };

  return { durationSeconds, sample };
};
